import React from 'react';
import { SEO } from '../../seo/seo';
import { useSelector } from 'react-redux';
import { waterbodyImgUrl } from '../../waterbody_img/waterbody_img';
import { WaterbodyDetail } from '@omniafishing/core';
import { getBaseUrl, getFullUrl } from '../../../redux/router';
import { waterbodyCountiesStatesString } from '../../search_universal/search_waterbody';
import { getGenericMetaDescription } from '../../../lib/waterbody';
import { formatToIsoDateZ } from '../../../lib/time';
import { FIRST_PUBLISHED_DATE } from '../waterbody_detail';
import _ from 'lodash';
import { RoutePaths } from '../../../routes';
import { MapQueryParams } from '../../map_default/map_default';

interface WaterbodyFishingReportsSEOProps {
  waterbodyDetail: WaterbodyDetail;
  lastUpdatedISODate: string;
}
export const WaterbodyFishingReportsSEO = (props: WaterbodyFishingReportsSEOProps) => {
  const { waterbodyDetail, lastUpdatedISODate } = props;
  const waterbodyUrl = useSelector(getFullUrl);
  const waterbodyImg = waterbodyImgUrl(waterbodyDetail, {
    imgSize: { width: 125, height: 125, padding: 10 },
    hires: false,
  });

  const { primary_name, meta_description, lat, lng, meta_title, species_known, species, locales } =
    waterbodyDetail;

  const waterbodyLocation = waterbodyCountiesStatesString(
    waterbodyDetail.locales.map((l) => l.state.abbr),
    _.flatten(waterbodyDetail.locales.map((l) => l.counties))
  );

  let waterbodyNameWithLocation = primary_name;

  if (waterbodyLocation) {
    waterbodyNameWithLocation += ` - ${waterbodyLocation}`;
  }

  const knownSpeciesNames = species_known
    ? species
        .slice(0, 3)
        .map((s) => s.display_name)
        .join(', ')
    : '';

  const amenityFeature = [];

  if (knownSpeciesNames) {
    amenityFeature.push({
      '@type': 'LocationFeatureSpecification',
      name: 'Popular Species',
      value: knownSpeciesNames,
    });
  }

  const waterbodyStateNames = locales.map((locale) => `${locale.state.name}`).join(', ');
  const waterbodyCountyNames = _.flatten(locales.map((locale) => locale.counties)).join(', ');
  const keywords = `Fishing, ${primary_name}, ${waterbodyStateNames}, ${waterbodyCountyNames}  ${knownSpeciesNames}`;

  const bodyOfWaterLdJsonType: Record<string, any> = {
    '@type': 'LakeBodyOfWater',
    name: waterbodyNameWithLocation,
    description: meta_description || getGenericMetaDescription(waterbodyDetail),
    url: waterbodyUrl,
    dateCreated: formatToIsoDateZ(FIRST_PUBLISHED_DATE),
    geo: {
      '@type': 'GeoCoordinates',
      latitude: lat,
      longitude: lng,
    },
    image: waterbodyImg,
    publicAccess: true,
    keywords,
  };

  if (amenityFeature.length) {
    bodyOfWaterLdJsonType.amenityFeature = amenityFeature;
  }

  if (lastUpdatedISODate) {
    bodyOfWaterLdJsonType.dateModified = lastUpdatedISODate;
  }

  const baseUrl = useSelector(getBaseUrl);
  const mapRoute = `${baseUrl}${RoutePaths.MAP}?${MapQueryParams.lat}=${lat}&${
    MapQueryParams.lng
  }=${lng}&${MapQueryParams.zoom}=${13}`; // hardcode zoom b/c only used for SEO

  const schema: Record<string, any> = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Place',
        geo: {
          '@type': 'GeoCoordinates',
          latitude: lat,
          longitude: lng,
        },
        url: waterbodyUrl,
        hasMap: mapRoute,
        name: waterbodyNameWithLocation,
        alternateName: meta_title,
        image: waterbodyImg,
        description: meta_description || getGenericMetaDescription(waterbodyDetail),
      },
      bodyOfWaterLdJsonType,
    ],
  };
  return (
    <SEO
      title={`${primary_name} Fishing Reports`}
      description={`Track the latest fishing reports for ${primary_name}.`}
      meta={[{ name: 'date', content: lastUpdatedISODate }]}
      schema={schema}
    />
  );
};
